import React, { FC, useMemo, useState } from 'react';
import useTranslations from '@hooks/useTranslations';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { BasketPriceDetailsProps } from '@components/BasketPriceDetails';
import BasketPaymentDetails from '@components/v2/BasketPaymentDetails';
import BasketPxDetails from '@components/BasketPxDetails';
import SummaryDealerDetails from '@components/SummaryDealerDetails';
import SummaryPriceDetails from '@components/SummaryPriceDetails';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from '../../context/featureSwitchApp';
import FinanceWidget from '@components/FinanceWidget';
import BasketMandatoryFeesTemplate from '@components/v2/BasketMandatoryFeesDetails';
import BasketFeesDetailsTemplate from '@components/v2/BasketFeesDetails';
import BasketOptionalPartsTemplate from '@components/v2/BasketOptionalPartsDetails';
import { hasMandatoryDealerFees, hasOptionalDealerFees } from '@utils/Deal.utils';
import { isMarketBE, isMarketIT, WALLBOX_TYPE } from 'src/constants/main';
import isEmpty from 'lodash/isEmpty';
import { Pages } from 'src/types/pages';
import BasketScrappageDetails from '@components/v2/BasketScrappageDetails';
import DealerComment from '@components/DealerComment';
import SummaryDiscountDetails from '@components/SummaryDiscountDetails';
import { usePersonalizedOffer } from '@hooks/usePersonalizedOffer';
import { useUserDuck } from '@hooks/useUserDuck';
import BasketUserDetails from '@components/v2/BasketUserDetails';
import { useJourneyType } from '@hooks/useJourneyType';
import WallboxSelect from '@components/WallboxSelect';
import { ITrimAttribute } from '../../interfaces/Car';

const BasketPriceDetailsTemplate: FC<BasketPriceDetailsProps> = ({
    className,
    isOpenPriceDetails,
    setIsOpenPriceDetails,
}) => {
    const { t } = useTranslations();
    const { carDetails, currentDeal } = useCarDetailsDuck();
    const { isUserLoggedInAsDealer } = useUserDuck();
    const { isRent } = useJourneyType();
    const { isPersonalizedDeal, hasPersonalizedPx } = usePersonalizedOffer();

    const [renderWidget, setRenderWidget] = useState<() => void>();

    const dealerFees = useMemo(
        () => (!isEmpty(currentDeal?.extraFields?.dealerFees) ? JSON.parse(currentDeal?.extraFields?.dealerFees) : []),
        [currentDeal?.extraFields?.dealerFees]
    );

    const handleFinanceWidgetBtnClick = () => {
        renderWidget && renderWidget();
    };

    const registerRenderCb = (cb: () => void) => {
        setRenderWidget(() => cb);
    };

    const hasDealer = currentDeal?.fullProductConfiguration?.dealers?.length > 0 || currentDeal?.dealerId;

    const hasComment = currentDeal?.extraFields?.comments;

    const hasWallboxAccessory = carDetails?.trimAttributes?.find((attr: ITrimAttribute) => attr.type === WALLBOX_TYPE);

    const isFinanceWidgetDisabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_DISABLED_FINANCE_WIDGET);
    const isDealerFeesEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_DEALER_FEES_ENABLED);
    const isCarteGriseEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_CARTE_GRISE_ENABLED);
    const isPxEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_PX_ENABLED);
    const isScrappageEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SHOW_SCRAPPAGE_MODAL);

    const arrayChildren = [
        !isRent && isScrappageEnabled && carDetails?.importInputs?.ecologicalBonusScrappage > 0 && (
            <BasketScrappageDetails />
        ),
        // eslint-disable-next-line react/jsx-key
        <BasketPaymentDetails />,
        (isPxEnabled || isUserLoggedInAsDealer || hasPersonalizedPx) && !isRent && (
            <BasketPxDetails />
        ),
        !isRent && isCarteGriseEnabled && <BasketFeesDetailsTemplate />,
        !isRent && isDealerFeesEnabled && hasMandatoryDealerFees(dealerFees) && <BasketMandatoryFeesTemplate />,
        hasWallboxAccessory && <WallboxSelect />,
        !isRent && isDealerFeesEnabled && hasOptionalDealerFees(dealerFees) && <BasketOptionalPartsTemplate showFees />,
        !isRent && carDetails?.accessories?.length > 0 && <BasketOptionalPartsTemplate showAccessories />,
        hasDealer && <SummaryDealerDetails page={Pages.basketV2} />,
        // eslint-disable-next-line react/jsx-key
        isRent && <BasketUserDetails />,
        // eslint-disable-next-line react/jsx-key
        !isRent && (isUserLoggedInAsDealer || hasComment) && <DealerComment withoutBorderTop={!!hasDealer} />,
        !isRent && !isFinanceWidgetDisabled && <FinanceWidget registerRenderCb={registerRenderCb} />,
    ].filter(Boolean);

    return (
        <div className={className}>
            <SummaryPriceDetails
                isBasket
                isOpenPriceDetails={isOpenPriceDetails}
                setIsOpenPriceDetails={setIsOpenPriceDetails}
                handleFinanceWidgetBtnClick={handleFinanceWidgetBtnClick}
            />
            {isPersonalizedDeal && <SummaryDiscountDetails />}
            {arrayChildren.map((item: JSX.Element, index) => {
                if (item && item?.type) {
                    const Component = item.type;
                    return <Component key={item.type} {...{ ...item.props, generatedIndex: index + 1 }} />;
                }
            })}
        </div>
    );
};

export default BasketPriceDetailsTemplate;
